import clsx from 'clsx'
import first from 'lodash/first'
import React, { FC, useCallback, useMemo } from 'react'

import { Image } from '@Components/CMS/Image'
import { Video } from '@Components/CMS/Video'
import { Link } from '@Components/Link'
import { MediaGalleryCardProps } from '@Components/MediaGalleryCard'
import { PageType } from '@Generated/graphql'

import styles from './MediaGalleryBanner.module.scss'

export interface MediaGalleryBannerProps{
  card: MediaGalleryCardProps
  openModal: (pageType?: PageType, id?: string) => void

}

export const MediaGalleryBanner: FC<MediaGalleryBannerProps> = ({ card, openModal }) => {
  const { caption, image, link, linkID, linkType, mobileImage, video } = card
  const mediaContent = useMemo(() => (
    <>
      {image && (
        <Image
          desktopImage={image}
          desktopMaxHeight={1200}
          mobileImage={mobileImage || undefined}
          type="fillContainer"
        />
      )}

      {video && (
        <Video
          blockIndex={5}
          caption={caption}
          desktopCropping="top"
          desktopMaxHeight="100"
          disableControls
          fillContainer
          settings={{ autoplay: true }}
          type="fill-container"
          videoId={video!.videoId}
          videoMobileId={video!.videoMobileId}
          videoType={video!.videoType}
        />
      )}
    </>
  ), [caption, image, mobileImage, video])

  const getLinkContent = useCallback(() => {
    if (!link) {
      return null
    }
    return (
      <Link
        analytics={{
          dest_page_type: linkType || '',
          dest_page_type_id: linkID || '',
          source_id: 'media_gallery_linked_banner',
        }}
        className={styles.pointer}
        href={link.url as string}
        target={link!.newTab ? '_blank' : undefined}
      >
        {mediaContent}
      </Link>
    )
  }, [link, linkID, linkType, mediaContent])

  const getModalContent = useCallback(() => {
    if (!linkType?.includes('modal')) {
      return null
    }
    const ref = first(card.link?.references || []) as { id?: string, type?: string }

    return (
      <button
        className={styles.pointer}
        onClick={() => openModal(PageType.Modal, ref?.id)}
        type="button"
      >
        {mediaContent}
      </button>
    )
  }, [card, linkType, mediaContent, openModal])

  const content = useMemo(() => {
    if (link?.url && !link.url.includes('modal')) {
      return getLinkContent()
    }
    if (link?.url?.includes('modal')) {
      return getModalContent()
    }
    return mediaContent
  }, [getLinkContent, getModalContent, link, mediaContent])

  return (
    <div className={clsx(styles.mediaGalleryBanner)}>
      {content}
    </div>
  )
}
